/**
 * Mock Data para Proyectos Inmobiliarios
 * Basado en la estructura real de owly-api
 *
 * Estructura: Proyecto → Etapas → Bloques → Unidades → Tipologías
 *
 * IMPORTANTE: Este archivo ahora importa los tipos desde el módulo SGI
 * Las interfaces aquí definidas son legacy y serán removidas gradualmente
 */

// Import new types from SGI module
export type {
  Project,
  Stage,
  Block,
  Unit,
  Typology,
  MediaItem,
  Amenity,
  AdditionalType,
  ComplementaryUnit,
  PriceList,
  ProjectMetrics,
  UnitStatus,
  ProjectStatus
} from '@/domain';

// Re-export helpers
export {
  calculateProjectMetrics,
  getUnitDisplay,
  formatPrice
} from '@/domain';

// Import types for local use
import type { Project, Typology, Unit, Block, Stage, UnitStatus } from '@/domain';

// ========================================
// TIPOLOGÍAS COMUNES
// ========================================

export const typologies: Typology[] = [
  {
    id: 'typ-001',
    code: 'A',
    name: 'Tipo A - Estudio',
    slug: 'tipo-a-estudio',
    stageId: 'stage-tv-001',
    bedrooms: '1',
    bathrooms: '1',
    targetFrame: '32',
    mediaItems: [
      {
        type: 'virtualTour',
        reference: 'virtualTour-1',
        url: 'https://owly.com.co/tours/sample/tipo-a'
      },
      {
        type: 'floorPlan',
        reference: 'floorPlan-1',
        url: 'https://owly-projects.s3.us-east-1.amazonaws.com/sample/tipo-a/floorplan.webp'
      }
    ],
    glbCode: 'sample-tipo-a',
    description: 'Apartamento tipo estudio con cocina integral'
  },
  {
    id: 'typ-002',
    code: 'B',
    name: 'Tipo B - 2 Alcobas',
    slug: 'tipo-b-2-alcobas',
    stageId: 'stage-tv-001',
    bedrooms: '2',
    bathrooms: '2',
    targetFrame: '40',
    mediaItems: [
      {
        type: 'virtualTour',
        reference: 'virtualTour-1',
        url: 'https://owly.com.co/tours/sample/tipo-b'
      },
      {
        type: 'floorPlan',
        reference: 'floorPlan-1',
        url: 'https://owly-projects.s3.us-east-1.amazonaws.com/sample/tipo-b/floorplan.webp'
      },
      {
        type: 'rotator',
        reference: 'rotator-1',
        url: 'https://owly.com.co/tours/sample/tipo-b/360'
      }
    ],
    glbCode: 'sample-tipo-b',
    description: 'Apartamento de 2 alcobas con balcón'
  },
  {
    id: 'typ-003',
    code: 'C',
    name: 'Tipo C - 3 Alcobas',
    slug: 'tipo-c-3-alcobas',
    stageId: 'stage-tv-001',
    bedrooms: '3',
    bathrooms: '2',
    targetFrame: '34',
    mediaItems: [
      {
        type: 'virtualTour',
        reference: 'virtualTour-1',
        url: 'https://owly.com.co/tours/sample/tipo-c'
      },
      {
        type: 'floorPlan',
        reference: 'floorPlan-1',
        url: 'https://owly-projects.s3.us-east-1.amazonaws.com/sample/tipo-c/floorplan.webp'
      }
    ],
    glbCode: 'sample-tipo-c',
    description: 'Apartamento de 3 alcobas con zona de lavandería'
  },
  {
    id: 'typ-004',
    code: 'D',
    name: 'Tipo D - Penthouse',
    slug: 'tipo-d-penthouse',
    stageId: 'stage-tv-001',
    bedrooms: '3',
    bathrooms: '3',
    targetFrame: '85',
    mediaItems: [
      {
        type: 'virtualTour',
        reference: 'virtualTour-1',
        url: 'https://owly.com.co/tours/sample/tipo-d'
      },
      {
        type: 'floorPlan',
        reference: 'floorPlan-1',
        url: 'https://owly-projects.s3.us-east-1.amazonaws.com/sample/tipo-d/floorplan.webp'
      }
    ],
    glbCode: 'sample-tipo-d',
    description: 'Penthouse con terraza privada'
  },
  {
    id: 'typ-005',
    code: 'E',
    name: 'Tipo E - Duplex',
    slug: 'tipo-e-duplex',
    stageId: 'stage-tv-001',
    bedrooms: '3',
    bathrooms: '3',
    targetFrame: '89',
    mediaItems: [
      {
        type: 'virtualTour',
        reference: 'virtualTour-1',
        url: 'https://owly.com.co/tours/sample/tipo-e'
      },
      {
        type: 'floorPlan',
        reference: 'floorPlan-1',
        url: 'https://owly-projects.s3.us-east-1.amazonaws.com/sample/tipo-e/floorplan-1.webp'
      },
      {
        type: 'floorPlan',
        reference: 'floorPlan-2',
        url: 'https://owly-projects.s3.us-east-1.amazonaws.com/sample/tipo-e/floorplan-2.webp'
      }
    ],
    glbCode: 'sample-tipo-e',
    description: 'Apartamento duplex con estudio'
  }
];

// ========================================
// PROYECTO 1: TORRE VERDE
// ========================================

const torreVerdeStages: Stage[] = [
  {
    id: 'stage-tv-001',
    name: 'Etapa 1 - Roble',
    code: 'E1',
    slug: 'torre-verde-1',
    stageNumber: '1',
    projectId: 'proj-001',
    closingDate: '2025-12-31',
    deliveryDate: '2025-12-31',
    description: 'Primera etapa del proyecto Torre Verde',
    housingTypology: 'NOVIS',
    separation: '5',
    downPayment: '30',
    discount: '0',
    projectedIncrease: '5',
    glbCode: 'torre-verde-1',
    mediaItems: []
  }
];

const torreVerdeBlocks: Block[] = [
  {
    id: 'block-tv-001',
    name: 'Torre Norte',
    code: 'TN',
    slug: 'torre-verde-torre-norte',
    description: 'Torre principal con vista norte',
    stageId: 'stage-tv-001',
    floors: 15,
    unitsPerFloor: 4,
    totalUnits: 60,
    targetFrame: '40',
    glbCode: 'torre-verde-tn',
    mediaItems: [],
    amenities: [
      {
        id: 'amenity-tv-001',
        name: 'Gimnasio',
        description: 'Gimnasio equipado con máquinas de última tecnología',
        slug: 'torre-verde-tn-gimnasio',
        icon: 'barbell',
        floor: 1,
        area: '150',
        type: 'block',
        parentId: 'block-tv-001',
        glbCode: 'amenity-gym',
        mediaItems: [
          {
            type: 'image',
            reference: 'image-1',
            url: 'https://owly-projects.s3.us-east-1.amazonaws.com/sample/amenities/gym.webp'
          }
        ]
      },
      {
        id: 'amenity-tv-002',
        name: 'Piscina',
        description: 'Piscina climatizada con jacuzzi',
        slug: 'torre-verde-tn-piscina',
        icon: 'swimming',
        floor: 2,
        area: '200',
        type: 'block',
        parentId: 'block-tv-001',
        glbCode: 'amenity-pool',
        mediaItems: []
      }
    ]
  },
  {
    id: 'block-tv-002',
    name: 'Torre Sur',
    code: 'TS',
    slug: 'torre-verde-torre-sur',
    description: 'Torre con vista sur y occidental',
    stageId: 'stage-tv-001',
    floors: 15,
    unitsPerFloor: 4,
    totalUnits: 60,
    targetFrame: '45',
    glbCode: 'torre-verde-ts',
    mediaItems: [],
    amenities: []
  }
];

// Generar unidades para Torre Verde
const generateTorreVerdeUnits = (): Unit[] => {
  const units: Unit[] = [];
  let unitCounter = 1;

  torreVerdeBlocks.forEach((block) => {
    for (let floor = 1; floor <= (block.floors || 15); floor++) {
      for (let unitNum = 1; unitNum <= (block.unitsPerFloor || 4); unitNum++) {
        const code = `${floor}${String(unitNum).padStart(2, '0')}`;
        const typologyIndex = unitNum % 4; // Rotar tipologías
        const typology = typologies[typologyIndex];

        // Calcular áreas realistas
        const privateArea = typology.bedrooms === '1' ? 36.59 :
                           typology.bedrooms === '2' ? 55.53 : 74.47;
        const balconyArea = floor > 10 ? 5.27 : 3.93;
        const terraceArea = floor === (block.floors || 15) ? 15 : 0; // Terraza solo en último piso
        const buildArea = privateArea + balconyArea + terraceArea;

        const basePrice = buildArea * 4500000; // $4.5M por m²
        const floorBonus = floor * 500000; // Bonus por piso
        const price = basePrice + floorBonus;

        // Mayor probabilidad de vendidas para generar más movimiento en gráficas
        const statuses: UnitStatus[] = ['available', 'available', 'reserved', 'sold', 'sold', 'sold', 'sold', 'optioned', 'blocked'];
        const status = statuses[Math.floor(Math.random() * statuses.length)];

        units.push({
          id: `unit-tv-${String(unitCounter).padStart(3, '0')}`,
          code,
          name: code,
          slug: `${block.slug}-${code}`,
          typologyId: typology.id,
          typologySlug: typology.slug,
          blockId: block.id,
          stageId: 'stage-tv-001',
          floor: String(floor),

          // Áreas detalladas
          privateArea: String(privateArea),
          buildArea: String(buildArea),
          balconyArea: String(balconyArea),
          terraceArea: String(terraceArea),
          terracePrivateArea: '0',
          mezzanineArea: '0',
          terraceNumber: terraceArea > 0 ? '1' : '0',
          balconyNumber: '1',

          bedrooms: typology.bedrooms,
          bathrooms: typology.bathrooms,

          // Pricing
          priceType: 'fixedPrice',
          price: String(price),
          fixedPrice: String(price),

          glbCode: `${block.slug}-${code}`,
          status,
          orientation: unitNum <= 2 ? 'Norte' : 'Sur',
          view: floor >= 10 ? 'Ciudad' : 'Interior',

          complementaryUnit: {},
          complementaryUnits: []
        });

        unitCounter++;
      }
    }
  });

  return units;
};

export const torreVerde: Project = {
  id: 'torre-verde',
  aiProfileId: 'bossco', // ✅ Perfil AI para comportamiento del asistente
  name: 'Torre Verde',
  slug: 'torre-verde',
  code: 'TV',
  companyId: 'comp-001', // Inmobiliaria Los Andes
  marketId: 'market-co', // ✅ Opera en Colombia
  description: 'Proyecto residencial de lujo en el corazón de la ciudad',

  // Location
  country: 'CO',
  city: 'Medellín',
  neighborhood: 'El Poblado',
  address: 'Calle 10 # 43A-25',
  location: {
    city: 'Medellín',
    neighborhood: 'El Poblado',
    address: 'Calle 10 # 43A-25'
  },

  // Status
  status: 'construction',
  startDate: '2024-01-15',
  estimatedDelivery: '2025-12-31',

  // Financial Configuration
  currency: 'COP',
  measureUnit: 'm²',
  interestRate: '6.5',
  showPrices: true,
  allowQuote: true,

  // Branding
  backgroundColor: '#2C8B4A',
  accentColor: '#F8DB66',
  statusColors: {
    available: '#41d152',
    sold: '#ff5b5b',
    reserved: '#ffab23',
    unavailable: '#ff2727',
    optioned: '#468eff',
    blocked: '#888888'
  },

  // Media
  mediaItems: [
    {
      type: 'image',
      reference: 'image-1',
      url: 'https://owly-projects.s3.us-east-1.amazonaws.com/torre-verde/project/main.webp'
    },
    {
      type: 'map',
      reference: 'map-1',
      url: 'https://api.mapbox.com/styles/v1/mapbox/streets-v11'
    }
  ],

  // 3D Visualization
  orbitViewProject: 'torre-verde-project',
  orbitViewAmenities: 'torre-verde-amenities',

  // Lead Scoring
  scoreWeights: {
    project: 20,
    location: 10,
    economic: 40,
    contact: 30
  },

  // CRM Integration
  crm: 'monday',
  crmConfig: JSON.stringify({
    boardId: '123456',
    apiKey: 'dummy-key'
  }),

  // Structure
  stages: torreVerdeStages,
  blocks: torreVerdeBlocks,
  units: generateTorreVerdeUnits(),
  typologies: typologies.slice(0, 4)
};

// ========================================
// PROYECTO 2: BOSQUES DEL RÍO
// ========================================

const bosquesRioStages: Stage[] = [
  {
    id: 'stage-br-001',
    name: 'Etapa 1',
    code: 'E1',
    slug: 'etapa-1',
    projectId: 'proj-002',
    deliveryDate: '2026-06-30'
  }
];

const bosquesRioBlocks: Block[] = [
  {
    id: 'block-br-001',
    name: 'Edificio Sauce',
    code: 'SAU',
    slug: 'edificio-sauce',
    stageId: 'stage-br-001',
    floors: 10,
    unitsPerFloor: 6,
    totalUnits: 60
  }
];

const generateBosquesRioUnits = (): Unit[] => {
  const units: Unit[] = [];
  let unitCounter = 1;

  bosquesRioBlocks.forEach((block) => {
    for (let floor = 1; floor <= (block.floors || 10); floor++) {
      for (let unitNum = 1; unitNum <= (block.unitsPerFloor || 6); unitNum++) {
        const code = `${floor}${String(unitNum).padStart(2, '0')}`;
        const typologyIndex = (unitNum - 1) % 3; // Rotar entre A, B, C
        const typology = typologies[typologyIndex];

        // Calcular áreas realistas
        const privateArea = typology.bedrooms === '1' ? 36.59 :
                           typology.bedrooms === '2' ? 55.53 : 74.47;
        const balconyArea = floor > 6 ? 5.27 : 3.93;
        const terraceArea = floor === (block.floors || 10) ? 15 : 0; // Terraza solo en último piso
        const buildArea = privateArea + balconyArea + terraceArea;

        const basePrice = buildArea * 4200000; // $4.2M por m²
        const floorBonus = floor * 400000;
        const price = basePrice + floorBonus;

        // Mayor probabilidad de vendidas para generar más movimiento en gráficas
        const statuses: UnitStatus[] = ['available', 'available', 'reserved', 'sold', 'sold', 'sold', 'sold', 'optioned', 'blocked'];
        const status = statuses[Math.floor(Math.random() * statuses.length)];

        units.push({
          id: `unit-br-${String(unitCounter).padStart(3, '0')}`,
          code,
          name: code,
          slug: `${block.slug}-${code}`,
          typologyId: typology.id,
          typologySlug: typology.slug,
          blockId: block.id,
          stageId: 'stage-br-001',
          floor: String(floor),

          // Áreas detalladas
          privateArea: String(privateArea),
          buildArea: String(buildArea),
          balconyArea: String(balconyArea),
          terraceArea: String(terraceArea),
          terracePrivateArea: '0',
          mezzanineArea: '0',
          terraceNumber: terraceArea > 0 ? '1' : '0',
          balconyNumber: '1',

          bedrooms: typology.bedrooms,
          bathrooms: typology.bathrooms,

          // Pricing
          priceType: 'fixedPrice',
          price: String(price),
          fixedPrice: String(price),

          glbCode: `${block.slug}-${code}`,
          status,
          orientation: unitNum <= 3 ? 'Oriente' : 'Occidente',
          view: floor >= 6 ? 'Río' : 'Bosque',

          complementaryUnit: {},
          complementaryUnits: []
        });

        unitCounter++;
      }
    }
  });

  return units;
};

export const bosquesRio: Project = {
  id: 'bosques-del-rio',
  aiProfileId: 'aurum', // ✅ Perfil AI de lujo para comportamiento del asistente
  name: 'Bosques del Río',
  slug: 'bosques-del-rio',
  code: 'BR',
  companyId: 'comp-002', // Constructora Pacifico
  marketId: 'market-co', // ✅ Opera en Colombia
  description: 'Apartamentos campestres con vista al río',
  location: {
    city: 'Envigado',
    neighborhood: 'Las Antillas',
    address: 'Carrera 48 # 32-15'
  },
  status: 'construction',
  startDate: '2024-06-01',
  estimatedDelivery: '2026-06-30',
  stages: bosquesRioStages,
  blocks: bosquesRioBlocks,
  units: generateBosquesRioUnits(),
  typologies: typologies.slice(0, 3)
};

// ========================================
// PROYECTO 3: CIUDAD JARDÍN
// ========================================

const ciudadJardinStages: Stage[] = [
  {
    id: 'stage-cj-001',
    name: 'Etapa 1',
    code: 'E1',
    slug: 'etapa-1',
    projectId: 'proj-003',
    deliveryDate: '2025-09-30'
  }
];

const ciudadJardinBlocks: Block[] = [
  {
    id: 'block-cj-001',
    name: 'Torre Jazmín',
    code: 'TJ',
    slug: 'torre-jazmin',
    stageId: 'stage-cj-001',
    floors: 12,
    unitsPerFloor: 3,
    totalUnits: 36
  }
];

const generateCiudadJardinUnits = (): Unit[] => {
  const units: Unit[] = [];
  let unitCounter = 1;

  ciudadJardinBlocks.forEach((block) => {
    for (let floor = 1; floor <= (block.floors || 12); floor++) {
      for (let unitNum = 1; unitNum <= (block.unitsPerFloor || 3); unitNum++) {
        const code = `${floor}${String(unitNum).padStart(2, '0')}`;
        // Usar tipologías más grandes (B, C, D)
        const typologyIndex = (unitNum % 3) + 1;
        const typology = typologies[typologyIndex];

        // Calcular áreas realistas para tipologías más grandes
        const privateArea = typology.bedrooms === '2' ? 55.53 :
                           typology.bedrooms === '3' ? 74.47 : 95.80;
        const balconyArea = floor > 8 ? 6.50 : 5.27;
        const terraceArea = floor === (block.floors || 12) ? 20 : 0; // Terraza solo en último piso
        const buildArea = privateArea + balconyArea + terraceArea;

        const basePrice = buildArea * 5000000; // $5M por m² (zona premium)
        const floorBonus = floor * 800000;
        const price = basePrice + floorBonus;

        // Mayor probabilidad de vendidas para generar más movimiento en gráficas
        const statuses: UnitStatus[] = ['available', 'available', 'reserved', 'sold', 'sold', 'sold', 'sold', 'optioned', 'blocked'];
        const status = statuses[Math.floor(Math.random() * statuses.length)];

        units.push({
          id: `unit-cj-${String(unitCounter).padStart(3, '0')}`,
          code,
          name: code,
          slug: `${block.slug}-${code}`,
          typologyId: typology.id,
          typologySlug: typology.slug,
          blockId: block.id,
          stageId: 'stage-cj-001',
          floor: String(floor),

          // Áreas detalladas
          privateArea: String(privateArea),
          buildArea: String(buildArea),
          balconyArea: String(balconyArea),
          terraceArea: String(terraceArea),
          terracePrivateArea: '0',
          mezzanineArea: '0',
          terraceNumber: terraceArea > 0 ? '1' : '0',
          balconyNumber: '1',

          bedrooms: typology.bedrooms,
          bathrooms: typology.bathrooms,

          // Pricing
          priceType: 'fixedPrice',
          price: String(price),
          fixedPrice: String(price),

          glbCode: `${block.slug}-${code}`,
          status,
          orientation: unitNum === 1 ? 'Norte' : unitNum === 2 ? 'Sur' : 'Oriente',
          view: floor >= 8 ? 'Panorámica' : 'Jardín',

          complementaryUnit: {},
          complementaryUnits: []
        });

        unitCounter++;
      }
    }
  });

  return units;
};

export const ciudadJardin: Project = {
  id: 'ciudad-jardin',
  aiProfileId: 'bossco', // ✅ Perfil AI genérico (fallback)
  name: 'Ciudad Jardín',
  slug: 'ciudad-jardin',
  code: 'CJ',
  companyId: 'comp-005', // Propiedades Premium
  marketId: 'market-co', // ✅ Opera en Colombia
  description: 'Exclusivo proyecto residencial con amplias zonas verdes',
  location: {
    city: 'Medellín',
    neighborhood: 'Laureles',
    address: 'Carrera 70 # 45-32'
  },
  status: 'construction',
  startDate: '2024-03-01',
  estimatedDelivery: '2025-09-30',
  stages: ciudadJardinStages,
  blocks: ciudadJardinBlocks,
  units: generateCiudadJardinUnits(),
  typologies: typologies.slice(1, 4)
};

// ========================================
// EXPORTACIONES
// ========================================

export const mockProjects: Project[] = [
  torreVerde,
  bosquesRio,
  ciudadJardin
];

// Función helper para obtener una unidad aleatoria de un proyecto
export const getRandomUnit = (projectSlug?: string): Unit => {
  const project = projectSlug
    ? mockProjects.find(p => p.slug === projectSlug)
    : mockProjects[Math.floor(Math.random() * mockProjects.length)];

  if (!project) return mockProjects[0].units[0];

  const availableUnits = project.units.filter(u => u.status === 'available');
  if (availableUnits.length === 0) return project.units[0];

  return availableUnits[Math.floor(Math.random() * availableUnits.length)];
};
